import type { ChordRef, Place } from "@bandstand/core/arrangeChords";
import type { Segment, SheetLine } from "@bandstand/core/chordPro";
import styled, { css, keyframes } from "styled-components";

import { color } from "../../theme/color";

/** What arranging shows on the sheet: the chord that's picked, and the one that's dragged. */
export type Arranging = {
    picked?: ChordRef;
    dragged?: ChordRef;
};

/**
 * One line of a chord sheet: lyrics with chords above them, lyrics only, or a remark.
 * Every word and chord says where it is (`data-line`, `data-at`, `data-nth`), so holding, tapping and dragging know
 * which one it was. A word is split where a chord is played in the middle of it. While arranging, the chords wiggle and every line has room for them.
 */
export function SheetLineView({ line, lyricsOnly, arranging }: { line: SheetLine; lyricsOnly: boolean; arranging?: Arranging }) {
    if (line.kind === "comment") return <Comment>{line.text}</Comment>;

    if (lyricsOnly) {
        const lyrics = line.segments.map((segment) => segment.lyrics).join("");

        return lyrics.trim() ? <Lyrics as="div">{lyrics}</Lyrics> : null;
    }

    const movable = line.line !== undefined;
    const showChords = line.segments.some((segment) => segment.chord) || (!!arranging && movable);
    const isAt = (spot: Place | undefined, at: number) => spot !== undefined && spot.line === line.line && spot.at === at;
    const same = (ref: ChordRef | undefined, at: number, nth: number) => isAt(ref, at) && ref?.nth === nth;
    const places = placesOf(line.segments);

    return (
        <MusicLine>
            {line.segments.map((segment, index) => {
                const place = places[index];

                return (
                    <Word key={index} data-line={movable ? line.line : undefined} data-at={movable ? place.at : undefined}>
                        {showChords &&
                            (segment.chord ? (
                                <Chord
                                    data-nth={movable ? place.nth : undefined}
                                    $arranging={!!arranging && movable}
                                    $picked={same(arranging?.picked, place.at, place.nth)}
                                    $dragged={same(arranging?.dragged, place.at, place.nth)}
                                    style={{ animationDelay: `${-(index % 5) * 0.07}s` }}
                                >
                                    {segment.chord}
                                </Chord>
                            ) : (
                                <NoChord> </NoChord>
                            ))}
                        <Lyrics data-lyrics>{segment.lyrics}</Lyrics>
                    </Word>
                );
            })}
        </MusicLine>
    );
}

/**
 * Where each segment starts in the line's lyrics, and which chord over that spot it holds: the second of `[G][D]` is
 * `nth` 1. This matches how `arrangeChords` counts them.
 */
function placesOf(segments: Segment[]): { at: number; nth: number }[] {
    let at = 0;
    let lastChordAt = -1;
    let nth = 0;

    return segments.map((segment) => {
        const start = at;

        at += segment.lyrics.length;

        if (segment.chord) {
            nth = start === lastChordAt ? nth + 1 : 0;
            lastChordAt = start;
        }

        return { at: start, nth };
    });
}

const wiggle = keyframes`
    from { transform: rotate(-3deg); }
    to { transform: rotate(3deg); }
`;

const MusicLine = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    margin-bottom: 0.15em;
`;

const Word = styled.span`
    display: inline-flex;
    flex-direction: column;
`;

const chordText = css`
    font-size: calc(0.85em * var(--chord-size, 1));
    font-weight: 700;
    line-height: 1.3;
    white-space: pre;
`;

const Chord = styled.span<{ $arranging: boolean; $picked: boolean; $dragged: boolean }>`
    ${chordText}
    align-self: flex-start;
    color: ${color.chord};
    padding-right: 0.35em;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;

    ${({ $arranging, $picked, $dragged }) =>
        $arranging &&
        css`
            margin-right: 0.2em;
            padding: 0 0.3em;
            border-radius: 6px;
            background: ${$picked ? color.accent : color.accentSofter};
            color: ${$picked ? color.onAccent : color.chord};
            outline: 1px solid ${color.accentSoft};
            opacity: ${$dragged ? 0.3 : 1};
            cursor: grab;
            touch-action: none;
            animation: ${wiggle} 0.16s ease-in-out infinite alternate;

            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }
        `}
`;

const NoChord = styled.span`
    ${chordText}
`;

const Lyrics = styled.span`
    font-family: var(--sheet-font, inherit);
    line-height: var(--line-spacing, 1.45);
    min-height: calc(var(--line-spacing, 1.45) * 1em);
    white-space: pre-wrap;
`;

const Comment = styled.p`
    margin: 0 0 0.4em;
    padding: 0.3em 0.6em;
    border-radius: 8px;
    background: ${color.surface};
    color: ${color.textSoft};
    font-style: italic;
    font-size: 0.85em;
`;
